/**
 * audio/context.js — AudioContext lifecycle, the output buses, and the
 * test-mode logging path.
 *
 * Three rules shape this file:
 *
 * 1. **Never construct an AudioContext at import time.** Browsers block audio
 *    until a user gesture; a context built at import lands in `suspended` and
 *    emits the console warning that would fail the no-errors smoke test. The
 *    context is created inside the first pointerdown/keydown/XR-select instead.
 * 2. **Under `?test=1` no AudioContext is created at all.** Every play call
 *    becomes a log entry in a ring buffer. Headless runs stay silent, fast and
 *    assertable, and `__cc.audio.played()` is the assertion surface.
 * 3. **One timer for everything.** Ambient beds and the music sequencer both
 *    register a tick callback here rather than each owning an interval.
 *
 * Bus topology:
 *
 *     recipes ──► sfx ─────┐
 *     music   ──► music ───┼──► master ──► destination
 *     beds    ──► ambient ─┘
 *
 * @module audio/context
 */

import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { save } from '../core/save.js';
import { logCall, options } from '../core/testmode.js';

/** Sub-buses that sit between a source and the master gain. */
export const BUS_NAMES = Object.freeze(['sfx', 'music', 'ambient']);

/** Under `?test=1` nothing is synthesised — calls are logged instead. */
export const LOG_ONLY = options.test;

/** Scheduler period. Ambient randomisation and the music lookahead ride this. */
const TICK_MS = 120;
const LOG_LIMIT = 400;
/** Above this many concurrent scheduled voices, new one-shots are dropped. */
const MAX_VOICES = 28;

const state = {
  ctx: null,
  master: null,
  /** @type {Record<string, GainNode>} */
  buses: {},
  unlocked: false,
  installed: false,
  muted: false,
  voices: 0,
  dropped: 0,
  /** @type {Array<{ name: string, opts: object, t: number }>} */
  played: [],
  /** Recipe names asked for that no recipe implements. */
  unknown: new Set(),
  volumes: { master: 1, sfx: 0.85, music: 0.6, ambient: 0.7 },
  timer: null,
  /** @type {Set<(now: number) => void>} */
  ticks: new Set(),
  /** Resolved when a real context exists; used by ambient/music to defer. */
  onUnlock: new Set(),
};

// ─────────────────────────────────────────────────────────────────────────────
// The played log — the test-mode assertion surface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Record a play request. Mirrored into `window.__ccCalls` so the pre-existing
 * stub-call assertions in other agents' tests keep working unchanged.
 *
 * Control entries (mute, ambient/music transitions) are prefixed `@` so a test
 * enumerating recipes can filter them out.
 *
 * @param {string} name
 * @param {object} [opts]
 * @returns {{ name: string, opts: object, t: number }} The stored entry.
 */
export function logPlay(name, opts = {}) {
  const entry = { name, opts: { ...opts }, t: Date.now() };
  state.played.push(entry);
  if (state.played.length > LOG_LIMIT) state.played.shift();
  if (!name.startsWith('@')) logCall('audio', 'play', [name, opts]);
  return entry;
}

/** @returns {Array<{ name: string, opts: object, t: number }>} */
export function played() {
  return state.played.map((e) => ({ ...e, opts: { ...e.opts } }));
}

/**
 * The most recent entry for a name, for de-dupe merging.
 * @param {string} name
 * @param {number} withinMs
 * @returns {{ name: string, opts: object, t: number }|null}
 */
export function lastPlay(name, withinMs) {
  for (let i = state.played.length - 1; i >= 0; i--) {
    const e = state.played[i];
    if (e.name !== name) continue;
    return Date.now() - e.t <= withinMs ? e : null;
  }
  return null;
}

/** Drop the played log. */
export function clearLog() {
  state.played.length = 0;
}

/** @param {string} name Record a request no recipe implements. */
export function noteUnknown(name) {
  state.unknown.add(name);
}

// ─────────────────────────────────────────────────────────────────────────────
// Context + buses
// ─────────────────────────────────────────────────────────────────────────────

/** @returns {AudioContext|null} */
export function context() {
  return state.ctx;
}

/** @returns {boolean} True once a real context exists and is running. */
export function isReady() {
  return !LOG_ONLY && !!state.ctx && state.ctx.state === 'running';
}

/** @returns {number} Scheduling clock, 0 when there is no context. */
export function now() {
  return state.ctx ? state.ctx.currentTime : 0;
}

/**
 * The gain node a source should connect to.
 * @param {'sfx'|'music'|'ambient'|'master'} name
 * @returns {GainNode|null}
 */
export function busNode(name) {
  if (!state.ctx) return null;
  if (name === 'master') return state.master;
  return state.buses[name] || state.buses.sfx;
}

/**
 * Create the context and bus graph. Only ever called from a gesture handler.
 * @returns {boolean} True if a context is live afterwards.
 */
export function ensureContext() {
  if (LOG_ONLY) return false;
  if (state.ctx) return true;

  const Ctor = typeof window !== 'undefined'
    ? (window.AudioContext || window.webkitAudioContext)
    : null;
  if (!Ctor) return false;

  try {
    state.ctx = new Ctor({ latencyHint: 'interactive' });
  } catch {
    return false;
  }

  state.master = state.ctx.createGain();
  state.master.gain.value = 0;                    // faded in below
  state.master.connect(state.ctx.destination);

  for (const name of BUS_NAMES) {
    const g = state.ctx.createGain();
    g.gain.value = state.volumes[name];
    g.connect(state.master);
    state.buses[name] = g;
  }

  syncFromSave();
  applyMasterGain(0.25);
  startScheduler();
  return true;
}

/**
 * Bring audio up. Safe to call repeatedly and from any gesture handler.
 * @returns {Promise<boolean>} Whether audio is live afterwards.
 */
export async function unlock() {
  if (LOG_ONLY) return false;
  if (!ensureContext()) return false;
  if (state.ctx.state === 'suspended') {
    try { await state.ctx.resume(); } catch { /* a later gesture will retry */ }
  }
  const live = state.ctx.state === 'running';
  if (live && !state.unlocked) {
    state.unlocked = true;
    for (const fn of [...state.onUnlock]) {
      try { fn(); } catch (err) { reportError(err); }
    }
  }
  return live;
}

/**
 * Run `fn` as soon as a real context exists — immediately if it already does.
 * How ambient and music defer their start until the first gesture.
 * @param {() => void} fn
 * @returns {() => void} Unsubscribe.
 */
export function whenUnlocked(fn) {
  if (state.unlocked) {
    try { fn(); } catch (err) { reportError(err); }
    return () => {};
  }
  state.onUnlock.add(fn);
  return () => state.onUnlock.delete(fn);
}

/** @returns {boolean} */
export function isUnlocked() {
  return state.unlocked;
}

// ─────────────────────────────────────────────────────────────────────────────
// Volume + mute
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {'master'|'sfx'|'music'|'ambient'} name
 * @param {number} value 0–1
 * @param {{ persist?: boolean }} [opts]
 */
export function setVolume(name, value, { persist = true } = {}) {
  const v = Math.max(0, Math.min(1, Number(value) || 0));
  if (name === 'master') state.volumes.master = v;
  else if (name in state.volumes) state.volumes[name] = v;
  else return;

  if (persist && save.data && save.data.settings) {
    if (name === 'music') save.data.settings.musicVolume = v;
    // The save schema has no separate ambient slider; ambient follows sfx.
    if (name === 'sfx') save.data.settings.sfxVolume = v;
    if (name === 'music' || name === 'sfx') save.markDirty();
  }
  applyVolumes();
}

/** @returns {{ master: number, sfx: number, music: number, ambient: number }} */
export function volumes() {
  return { ...state.volumes };
}

/** Re-read the save's settings. Cheap, so it also runs on the scheduler tick. */
export function syncFromSave() {
  const s = save.data && save.data.settings;
  if (!s) return;
  if (typeof s.musicVolume === 'number') state.volumes.music = clamp01(s.musicVolume);
  if (typeof s.sfxVolume === 'number') {
    state.volumes.sfx = clamp01(s.sfxVolume);
    // Ambient sits under the SFX slider at a fixed ratio — the carnival bed
    // should never be louder than the sounds the player caused.
    state.volumes.ambient = clamp01(s.sfxVolume * 0.8);
  }
  applyVolumes();
}

/** @private Push the current volumes into the live gain nodes. */
function applyVolumes() {
  if (!state.ctx) return;
  const t = state.ctx.currentTime;
  for (const name of BUS_NAMES) {
    const g = state.buses[name];
    if (g) g.gain.setTargetAtTime(state.volumes[name], t, 0.03);
  }
  applyMasterGain(0.05);
}

/** @private @param {number} tau Ramp time constant. */
function applyMasterGain(tau) {
  if (!state.master || !state.ctx) return;
  const target = state.muted ? 0 : state.volumes.master;
  state.master.gain.setTargetAtTime(target, state.ctx.currentTime, tau);
}

/**
 * Mute everything — a backgrounded tab must go silent.
 * @param {boolean} muted
 */
export function setMuted(muted) {
  if (state.muted === muted) return;
  state.muted = muted;
  logPlay(muted ? '@mute' : '@unmute', { hidden: muted });
  applyMasterGain(0.02);
}

/** @returns {boolean} */
export function isMuted() {
  return state.muted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Voice accounting — one bad frame must not spawn 300 oscillators
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reserve a voice slot for `seconds`.
 * @param {number} seconds
 * @returns {boolean} False when the polyphony budget is exhausted.
 */
export function takeVoice(seconds) {
  if (state.voices >= MAX_VOICES) {
    state.dropped += 1;
    return false;
  }
  state.voices += 1;
  const ms = Math.max(40, Math.min(8000, seconds * 1000 + 120));
  setTimeout(() => { state.voices = Math.max(0, state.voices - 1); }, ms);
  return true;
}

/** @returns {number} */
export function voiceCount() {
  return state.voices;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scheduler — one interval shared by ambient randomisation and music lookahead
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {(now: number) => void} fn Called every ~120 ms with `ctx.currentTime`.
 * @returns {() => void} Unsubscribe.
 */
export function onTick(fn) {
  state.ticks.add(fn);
  return () => state.ticks.delete(fn);
}

/** @private */
function startScheduler() {
  if (state.timer || typeof window === 'undefined') return;
  let sinceSync = 0;
  state.timer = window.setInterval(() => {
    if (!state.ctx) return;
    const t = state.ctx.currentTime;
    sinceSync += TICK_MS;
    if (sinceSync >= 1000) { sinceSync = 0; syncFromSave(); }
    for (const fn of [...state.ticks]) {
      try { fn(t); } catch (err) { reportError(err); }
    }
  }, TICK_MS);
}

/** Stop the scheduler and release the context. Used by teardown/tests only. */
export function dispose() {
  if (state.timer) { clearInterval(state.timer); state.timer = null; }
  state.ticks.clear();
  if (state.ctx) {
    try { state.ctx.close(); } catch { /* already closed */ }
  }
  state.ctx = null;
  state.master = null;
  state.buses = {};
  state.unlocked = false;
  state.voices = 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Gesture unlock + page visibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wire the autoplay-policy escape hatches. Idempotent.
 *
 * Listeners are `once`-free on purpose: the first gesture may arrive while the
 * page is still loading and `resume()` can be rejected, so every subsequent
 * gesture gets another attempt until the context is genuinely running.
 */
export function installGestureUnlock() {
  if (state.installed || typeof window === 'undefined') return;
  state.installed = true;

  const onGesture = () => {
    if (state.unlocked) { detach(); return; }
    unlock().then((live) => { if (live) detach(); });
  };
  const evts = ['pointerdown', 'mousedown', 'touchstart', 'keydown'];
  const detach = () => {
    for (const e of evts) window.removeEventListener(e, onGesture, true);
  };
  for (const e of evts) window.addEventListener(e, onGesture, true);

  // Entering a WebXR session is itself a gesture-privileged transition.
  bus.on(EVENTS.XR_SESSION_START, () => { unlock(); });

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      setMuted(!!document.hidden);
    });
  }

  // The save's volume sliders may be changed by any agent's settings UI; the
  // debounced write is the signal that something changed.
  bus.on(EVENTS.SAVE_WRITE, () => syncFromSave());
  syncFromSave();
}

/**
 * Report an internal audio failure without ever throwing into the frame loop.
 * @param {unknown} err
 */
export function reportError(err) {
  if (typeof window !== 'undefined' && Array.isArray(window.__ccErrors)) {
    window.__ccErrors.push(`audio: ${err && err.message ? err.message : err}`);
  }
}

/** @param {number} v @returns {number} */
function clamp01(v) {
  return Math.max(0, Math.min(1, Number(v) || 0));
}

/** Diagnostic snapshot for `__cc.audio.state()`. */
export function snapshot() {
  return {
    logOnly: LOG_ONLY,
    ready: isReady(),
    unlocked: state.unlocked,
    contextState: state.ctx ? state.ctx.state : 'none',
    sampleRate: state.ctx ? state.ctx.sampleRate : 0,
    muted: state.muted,
    volumes: volumes(),
    /** Live gain values read back off the nodes — proves a set actually applied. */
    busGain: state.ctx
      ? {
        master: state.master.gain.value,
        sfx: state.buses.sfx.gain.value,
        music: state.buses.music.gain.value,
        ambient: state.buses.ambient.gain.value,
      }
      : null,
    voices: state.voices,
    dropped: state.dropped,
    logged: state.played.length,
    unknown: [...state.unknown],
  };
}
